import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, delay, distinctUntilChanged, filter, firstValueFrom, map } from "rxjs";
import { environment } from "src/environments/environment";
import { TOKEN } from "../const/constant";
import { AuthenticationService } from "./auth.service";
import { LocalStorageService } from "./local-storage.service";
import { isRunningAsInstalledApp } from "./pwa-install.service";

export type PwaInstallSource = "appinstalled" | "standalone";
export type PwaDeviceType = "mobile" | "tablet" | "desktop";

export interface PwaInstallation {
	id: number;
	deviceId: string;
	/** Date d'installation (source "appinstalled") ou de première ouverture en mode installé (source "standalone"). */
	installedAt: string;
	lastSeenAt: string;
	launchCount: number;
	source: PwaInstallSource;
	deviceType: PwaDeviceType | null;
	os: string | null;
	osVersion: string | null;
	model: string | null;
	browser: string | null;
	browserVersion: string | null;
	userAgent: string | null;
	user: { id: number; username: string; email: string; employe?: { id: number; nom: string; prenom: string } | null } | null;
	userLabel: string | null;
}

export interface PwaInstallationFilter {
	search?: string;
	deviceType?: PwaDeviceType | "";
	os?: string;
	source?: PwaInstallSource | "";
	from?: string;
	to?: string;
	page?: number;
	pageSize?: number;
}

export interface PwaInstallationPage {
	data: PwaInstallation[];
	meta: { pagination: { page: number; pageSize: number; total: number; pageCount: number } };
}

/** Indices lus dans le navigateur : Chrome ne donne plus le modèle ni la version d'Android dans le User-Agent. */
interface DeviceHints {
	platform?: string;
	platformVersion?: string;
	model?: string;
	mobile?: boolean;
	touch?: boolean;
}

/** Identifie ce navigateur sur cet appareil (UUID) : sans lui, chaque ouverture ressemblerait à une nouvelle installation. */
const DEVICE_ID_KEY = "pwa_device_id";
/** Installation constatée alors que personne n'était connecté : à déclarer dès la prochaine connexion. */
const PENDING_INSTALL_KEY = "pwa_installation_pending";

@Injectable({ providedIn: "root" })
export class PwaInstallationService {
	private apiURL = environment.apiURL;

	private trackingStarted = false;
	private isSending = false;
	/** Utilisateur dont l'ouverture en mode installé a déjà été déclarée pendant cette session. */
	private openingDeclaredFor: number | null = null;

	constructor(
		private http: HttpClient,
		private authenticationService: AuthenticationService,
		private localStorageService: LocalStorageService
	) {}

	/** Liste des installations (administrateurs). */
	list(filter: PwaInstallationFilter): Observable<PwaInstallationPage> {
		let params = new HttpParams();
		Object.entries(filter).forEach(([key, value]) => {
			if (value !== undefined && value !== null && value !== "") params = params.set(key, String(value));
		});
		return this.http.get<PwaInstallationPage>(`${this.apiURL}/pwa-installations`, { params });
	}

	/**
	 * Prévient le serveur quand l'application est installée sur cet appareil par l'utilisateur connecté :
	 * - au moment de l'installation (évènement « appinstalled » de Chrome, Edge, Android) ;
	 * - à chaque ouverture en mode installé, seul signal possible sur iPhone et seul moyen de retrouver
	 *   les installations faites avant la mise en place de ce suivi.
	 * À appeler une fois, au démarrage de l'application.
	 */
	startTracking(): void {
		if (this.trackingStarted) return;
		this.trackingStarted = true;

		window.addEventListener("appinstalled", () => {
			this.setPendingInstall();
			this.declare();
		});

		this.authenticationService.currentUser$
			.pipe(
				map((user) => user?.id ?? null),
				filter((id): id is number => id !== null),
				distinctUntilChanged(),
				// Au login, l'utilisateur est publié juste avant l'enregistrement du jeton : on attend la fin du tick,
				// sans quoi la requête partirait sans authentification.
				delay(0)
			)
			.subscribe(() => this.declare());
	}

	// Appelée sans await (évènement, abonnement) : elle ne doit jamais rejeter, ce suivi ne doit rien casser.
	private async declare(): Promise<void> {
		if (this.isSending) return;

		this.isSending = true;
		try {
			const userId = this.authenticationService.currentUserSubject.value?.id;
			if (!userId || !this.hasToken()) return;

			if (this.hasPendingInstall() && (await this.send("appinstalled"))) {
				this.clearPendingInstall();
			}
			if (isRunningAsInstalledApp() && this.openingDeclaredFor !== userId) {
				if (await this.send("standalone")) this.openingDeclaredFor = userId;
			}
		} catch (error) {
			console.warn("[pwa] suivi de l'installation interrompu", error);
		} finally {
			this.isSending = false;
		}
	}

	private hasToken(): boolean {
		try {
			return !!this.localStorageService.get(TOKEN);
		} catch {
			return false;
		}
	}

	/** true si le serveur a bien enregistré. Jamais d'erreur affichée : ce suivi ne doit pas gêner l'utilisateur. */
	private async send(source: PwaInstallSource): Promise<boolean> {
		const deviceId = this.getDeviceId();
		if (!deviceId) return false;

		try {
			await firstValueFrom(this.http.post(`${this.apiURL}/pwa-installations/register`, { deviceId, source, hints: await this.readHints() }));
			return true;
		} catch (error) {
			console.warn("[pwa] déclaration de l'installation impossible", error);
			return false;
		}
	}

	private async readHints(): Promise<DeviceHints> {
		const hints: DeviceHints = { touch: navigator.maxTouchPoints > 1 };
		const uaData = (navigator as Navigator & { userAgentData?: any }).userAgentData;
		if (!uaData) return hints;

		hints.mobile = uaData.mobile;
		hints.platform = uaData.platform;
		try {
			const high = await uaData.getHighEntropyValues(["model", "platformVersion"]);
			hints.model = high.model;
			hints.platformVersion = high.platformVersion;
		} catch {
			// le navigateur refuse : on garde ce qu'on a
		}
		return hints;
	}

	// localStorage peut être indisponible : sans identifiant stable, on ne déclare rien plutôt que de créer des doublons
	private getDeviceId(): string | null {
		try {
			let id = this.localStorageService.get(DEVICE_ID_KEY);
			if (!id) {
				id = crypto.randomUUID();
				this.localStorageService.set(DEVICE_ID_KEY, id);
			}
			return id;
		} catch {
			return null;
		}
	}

	private hasPendingInstall(): boolean {
		try {
			return this.localStorageService.get(PENDING_INSTALL_KEY) !== null;
		} catch {
			return false;
		}
	}

	private setPendingInstall(): void {
		try {
			this.localStorageService.set(PENDING_INSTALL_KEY, new Date().toISOString());
		} catch {
			// rien : l'ouverture en mode installé déclarera l'appareil
		}
	}

	private clearPendingInstall(): void {
		try {
			this.localStorageService.remove(PENDING_INSTALL_KEY);
		} catch {
			// rien
		}
	}
}
